import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Switch } from '@/common/ui/switch'
import { Badge } from '@/common/ui/badge'
import { Loader2, CheckCircle2, XCircle } from 'lucide-react'
import { toast } from 'sonner'
import showErrorToast from '@/common/utils/showErrorToast'
import useMutationUpdateOtpProvider from '@/modules/otp-provider/api/hooks/useMutationUpdateOtpProvider'
import { OTP_PROVIDER_QUERY_KEYS } from '@/modules/otp-provider/constants'
import LoadingOverlay from '@/common/components/LoadingOverlay'
import { cn } from '@/common/lib/utils'
import type { components } from '@/schema'

type OtpProvider = components['schemas']['AdminOtpProviderResponse']

interface ToggleOtpProviderStatusProps {
	provider: OtpProvider
}

export default function ToggleOtpProviderStatus({
	provider,
}: ToggleOtpProviderStatusProps) {
	const { mutateAsync: updateOtpProvider, isPending } =
		useMutationUpdateOtpProvider()
	const queryClient = useQueryClient()
	const [isLoading, setIsLoading] = useState(false)
	const isActive = provider.isActive

	const handleChange = async (checked: boolean) => {
		setIsLoading(true)
		try {
			await updateOtpProvider({
				id: provider.id,
				body: {
					isActive: checked,
				},
			})
			queryClient.invalidateQueries({
				queryKey: OTP_PROVIDER_QUERY_KEYS.PROVIDERS.ALL,
			})
			toast.success('อัปเดตสถานะสำเร็จ')
		} catch (error) {
			showErrorToast(error as Error)
		} finally {
			setIsLoading(false)
		}
	}

	return (
		<>
			<LoadingOverlay
				open={isPending || isLoading}
				message="กำลังอัปเดตสถานะผู้ให้บริการ OTP..."
			/>
			<div className="flex items-center gap-3">
				<Switch
					checked={isActive}
					onCheckedChange={handleChange}
					disabled={isLoading || isPending}
					className="data-[state=checked]:bg-green-600"
				/>
				{isLoading || isPending ? (
					<Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
				) : (
					<Badge
						variant={isActive ? 'default' : 'secondary'}
						className={cn(
							'flex items-center gap-1.5 px-2 py-0.5',
							isActive
								? 'bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-200'
								: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
						)}
					>
						{isActive ? (
							<CheckCircle2 className="h-3.5 w-3.5" />
						) : (
							<XCircle className="h-3.5 w-3.5" />
						)}
						<span className="text-xs font-medium">
							{isActive ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}
						</span>
					</Badge>
				)}
			</div>
		</>
	)
}
